<script>
  import { onMount } from 'svelte';
  import { format } from 'svelte-i18n';
  import { useNavigate } from 'svelte-navigator';
  import { Button, Container, Dropdown, DropdownItem, DropdownMenu, DropdownToggle } from '@sveltestrap/sveltestrap';

  import { BrandWizard } from '@client/components/brand-wizard';
  import { EmptyState } from '@client/components/empty-state';
  import { FilterBar } from '@client/components/filter-bar';
  import { SearchFilter } from '@client/components/filters/search';
  import { SortFilter } from '@client/components/filters/sort';
  import { Loader } from '@client/components/loader';
  import { NoMatches } from '@client/components/no-matches';
  import { getNotificationsContext } from '@client/components/notifications';
  import { PageHeader } from '@client/components/page';
  import { Pagination } from '@client/components/pagination';
  import { RenameModal } from '@client/components/rename-modal';

  import { BackgroundPreview } from '@client/pages/brandkit/components';

  import { hasBranding } from '@client/stores/brand';
  import { userStore } from '@client/stores/user';
  import {
    backgrounds,
    cloneBackground,
    deleteBackground,
    filteredRecords,
    filters,
    hasBackgrounds,
    initializeFilters,
    loadBackgrounds,
    onChangePage,
    onChangeRecords,
    onSearchFilterChange,
    onSortChange,
    renameBackground,
    totalRecords,
    updating
  } from '@client/stores/virtual-backgrounds';

  import { QueryParams } from '@client/utils/query-params';
  import { generateUniqueRulename } from '@client/utils/rulenames';

  import NewBackgroundModal from './designer/new-background-modal.svelte';

  const { addNotification } = getNotificationsContext();

  const navigate = useNavigate();
  const redirect = (path) => navigate(path);

  const pageQueryParam = parseInt(QueryParams.get('page'), 10);
  const perPageQueryParam = parseInt(QueryParams.get('perpage'), 10);
  const searchQueryParam = QueryParams.get('search') || '';

  let showFilters = true;
  let showNewBackgroundModal = false;
  let showBrandWizardModal = false;
  let showRenameModal = false;
  let renameItem = null;

  const onError = (text) => {
    addNotification({
      text,
      type: 'error',
      autohide: 2500,
      position: 'top-right'
    });
  };

  const onCreateNew = async () => {
    showNewBackgroundModal = true;
  };

  const onEdit = (background) => {
    redirect(`edit/${background.rulename}`);
  };

  const handleClone = async (background) => {
    const result = await cloneBackground(background, generateUniqueRulename);

    if (!result.success) {
      return onError($format('label.FAILURE.CLONE_BACKGROUND', { values: { rulename: background.rulename } }));
    }

    addNotification({
      text: $format('label.SUCCESS.CLONE', { values: { type: background.rulename } }),
      type: 'success',
      autohide: 2500,
      position: 'top-right'
    });

    setTimeout(() => redirect(`/brandkit/studio/edit/${result.rulename}`), 200);
  };

  const handleRename = async (background, newName) => {
    const result = await renameBackground(background, newName);

    if (!result.success) {
      return addNotification({
        text: $format('label.FAILURE.BACKGROUND_RENAME', { values: { rulename: background.rulename } }),
        type: 'error',
        autohide: 2500,
        position: 'top-right'
      });
    }

    addNotification({
      text: $format('label.SUCCESS.RENAME', { values: { from: background.rulename, to: newName } }),
      type: 'success',
      autohide: 2500,
      position: 'top-right'
    });

    showRenameModal = false;
  };

  const handleDelete = async (background) => {
    const confirmed = confirm(
      $format('label.DELETE_PROMPT', { values: { type: $format('label.BACKGROUND').toLowerCase() } })
    );

    if (confirmed) {
      const result = await deleteBackground(background);

      if (!result.success) {
        return onError($format('label.FAILURE.DELETE_BACKGROUND', { values: { rulename: background.rulename } }));
      }

      addNotification({
        text: $format('label.SUCCESS.DELETE', { values: { type: background.rulename } }),
        type: 'success',
        autohide: 2500,
        position: 'top-right'
      });
    }
  };

  onMount(async () => {
    initializeFilters(pageQueryParam, perPageQueryParam, searchQueryParam);
    await loadBackgrounds();
  });
</script>

<div id="studio" class="page">
  <Loader show={$updating} backdrop={true} />
  <PageHeader
    title={$format('label.DESIGNER_STUDIO')}
    paths={[[$format('label.EMPLOYEE_BRANDING'), '/brandkit/studio'], [$format('label.DESIGNER_STUDIO')]]}
    class="shadow-none"
  >
    <Button
      id="setup-branding"
      class="me-2 hide--mobile"
      color="secondary"
      on:click={() => (showBrandWizardModal = true)}
    >
      {$format($hasBranding ? 'label.UPDATE_BRANDING' : 'label.SETUP_BRANDING')}
    </Button>
    <Button id="add-background" color="secondary" on:click={onCreateNew}>
      {$format('label.NEW_BACKGROUND')}
    </Button>
  </PageHeader>

  <div class="page-content">
    {#if $hasBackgrounds}
      <div class="filters">
        <FilterBar bind:show={showFilters}>
          <SearchFilter bind:search={$filters.search} onChange={onSearchFilterChange} />
          <SortFilter
            bind:filter={$filters.sorting.options}
            bind:sortDirection={$filters.sorting.dir}
            onClick={onSortChange}
            sortDirParam="sortDir"
            sortKeyParam="sortKey"
          />
        </FilterBar>
      </div>
    {/if}
    <Container fluid>
      {#if $hasBackgrounds}
        {#if $filteredRecords.length}
          <div class="backgrounds">
            {#each $filteredRecords as background (background._id)}
              <div class="background" on:click={() => onEdit(background)} role="button" tabindex="0">
                <div class="background--header">
                  <div class="background--rule">
                    <span title={background.rulename} class:active={background.active}>{background.rulename}</span>

                    {#if background.active}
                      <div class="background--status">{$format('label.IN_USE')}</div>
                    {/if}
                  </div>

                  <div class="background--actions" on:click|preventDefault|stopPropagation role="button" tabindex="0">
                    <Dropdown>
                      <DropdownToggle tag="div" class="os-icon-more-horizontal" />
                      <DropdownMenu>
                        <DropdownItem
                          on:click={() => {
                            renameItem = background;
                            showRenameModal = true;
                          }}
                        >
                          {$format('label.RENAME')}
                        </DropdownItem>
                        <DropdownItem divider />
                        <DropdownItem on:click={() => onEdit(background)}>{$format('label.EDIT')}</DropdownItem>
                        <DropdownItem on:click={() => handleClone(background)}>{$format('label.CLONE')}</DropdownItem>
                        {#if !background.active}
                          <DropdownItem divider />
                          <DropdownItem on:click={() => handleDelete(background)}>
                            {$format('label.DELETE')}
                          </DropdownItem>
                        {/if}
                      </DropdownMenu>
                    </Dropdown>
                  </div>
                </div>
                <div class="background--preview" on:click={() => onEdit(background)} role="button" tabindex="0">
                  <div style:height="108px" style:width="192px">
                    <BackgroundPreview
                      width={1920}
                      height={1080}
                      background={background.template.background}
                      foreground={background.template.foreground}
                      cells={background.template.cells}
                      user={$userStore.currentUser}
                    />
                  </div>
                </div>
              </div>
            {/each}
          </div>
        {:else}
          <div class="mt-3">
            <NoMatches message={$format('label.MESSAGE.NO_MATCHES_BACKGROUNDS')} />
          </div>
        {/if}
      {:else}
        <EmptyState
          icon="monitor"
          heading={$format('label.EMPTY_STATE.BACKGROUND_HEADING')}
          description={$format('label.EMPTY_STATE.BACKGROUND_DESCRIPTION')}
          action={$format('label.EMPTY_STATE.BACKGROUND_ACTION')}
          on:click={onCreateNew}
        />
      {/if}
    </Container>
  </div>

  {#if $totalRecords > 0}
    <div class="page-footer">
      <Pagination
        bind:currentPage={$filters.paging.page}
        bind:perPage={$filters.paging.perPage}
        totalRecords={$totalRecords}
        {onChangeRecords}
        {onChangePage}
      />
    </div>
  {/if}
</div>

{#if showNewBackgroundModal}
  <NewBackgroundModal bind:show={showNewBackgroundModal} />
{/if}

{#if showBrandWizardModal}
  <BrandWizard bind:show={showBrandWizardModal} />
{/if}

<RenameModal
  bind:show={showRenameModal}
  data={renameItem}
  name={renameItem?.rulename ?? ''}
  existingNames={$backgrounds.map((b) => b.rulename)}
  duplicateError={$format('label.RENAME_MODAL.ERROR.DUPLICATE_BACKGROUND')}
  onUpdate={handleRename}
/>

<style lang="scss" src="./studio.scss"></style>
